import {
  ChangeEvent,
  FC,
  createElement,
  forwardRef,
  useCallback,
  useMemo,
  useState,
} from "react";
import { QuestionDropdownModel, RendererFactory } from "survey-core";
import { ReactQuestionFactory } from "survey-react-ui";
import * as Select from "@radix-ui/react-select";
import { Check, ChevronDown } from "../icons";
import { useBreakpoint } from "../../hooks";
import cn from "../../utils/cn";

interface DropdownLiteQuestionProps {
  question: QuestionDropdownModel;
}

const DropdownLiteQuestion: FC<DropdownLiteQuestionProps> = ({ question }) => {
  const {
    value,
    isReadOnly,
    allowClear,
    placeholder,
    otherItem,
    noneItem,
    hasOther,
    hasNone,
    isOtherSelected,
    commentPlaceHolder,
    cssClasses,
  } = question;
  const { isAboveMd } = useBreakpoint("md");
  const [textAreaInput, setTextAreaInput] = useState(question.comment);

  const isDisabled = useMemo(() => {
    return isReadOnly;
  }, [isReadOnly]);

  const onValueChange = useCallback(
    (newVal: string) => {
      question.value = newVal;
    },
    [question]
  );

  const onCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setTextAreaInput(event.target.value);
  };

  const onCommentBlur = () => {
    question.comment = textAreaInput;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getItems = useMemo(() => {
    const items = question.dataChoices.map(({ title, value }) => ({
      title,
      value,
    }));
    if (hasNone) items.push(noneItem);
    if (hasOther) items.push(otherItem);
    if (allowClear) items.unshift({ value: undefined, title: "" });
    return items;
  }, [
    question.dataChoices,
    hasNone,
    noneItem,
    hasOther,
    otherItem,
    allowClear,
  ]);

  return (
    <>
      <Select.Root
        value={!value ? "" : value}
        onValueChange={onValueChange}
        name={question.name}
        required={question.isRequired}
        disabled={isDisabled}
        defaultValue={question.getDefaultValue()}
        autoComplete={question.autocomplete}
        aria-required={question.ariaRequired}
        aria-label={question.ariaLabel}
        aria-invalid={question.ariaInvalid}
        aria-errormessage={question.ariaErrormessage}
      >
        <Select.Trigger className="flex h-[53px] w-full items-center justify-between whitespace-nowrap py-4 bg-transparent border-b border-b-gray-200 text-sm font-normal text-gray-500 disabled:cursor-not-allowed focus:outline-none data-[placeholder]:text-gray-300">
          <div className="line-clamp-1">
            <Select.Value placeholder={placeholder} />
          </div>
          <Select.Icon>
            <ChevronDown
              color={isDisabled ? "var(--gray-300)" : "var(--gray-500)"}
              size={isAboveMd ? 20 : 18}
              className="group-data-[state=open]:rotate-180"
            />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content
            position="popper"
            className="z-50 overflow-hidden bg-white border border-gray-100 rounded-lg shadow-lg w-[var(--radix-select-trigger-width)] max-h-[var(--radix-select-content-available-height)]"
          >
            <Select.Viewport>
              {getItems?.map((item, index) => (
                <SelectItem key={item.value + "-" + index} value={item.value}>
                  {item.title}
                </SelectItem>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      {isOtherSelected && (
        <div className={cssClasses.commentArea}>
          <textarea
            disabled={isDisabled}
            onChange={onCommentChange}
            onBlur={onCommentBlur}
            className={cssClasses.comment}
            value={textAreaInput}
            placeholder={commentPlaceHolder}
          />
        </div>
      )}
    </>
  );
};

const SelectItem = forwardRef(
  ({ children, dense, className, disabled, ...props }: any, forwardedRef) => {
    return (
      <Select.Item
        key={props.key}
        className={cn(
          "flex relative gap-2 justify-between items-center py-2 w-full text-xs font-medium text-gray-800 cursor-pointer data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:outline-none data-[highlighted]:bg-gray-50 px-[14px] md:text-sm leading-[16px] md:leading-5 min-h-[38px] md:min-h-[40px]",
          className
        )}
        ref={forwardedRef}
        disabled={disabled}
        {...props}
      >
        <Select.ItemText>{children}</Select.ItemText>

        <Select.ItemIndicator>
          <Check size={20} color="var(--primary-600)" />
        </Select.ItemIndicator>
      </Select.Item>
    );
  }
);

ReactQuestionFactory.Instance.registerQuestion(
  "sv-dropdown-lite-question",
  function (props: any) {
    return createElement(DropdownLiteQuestion, props);
  }
);

RendererFactory.Instance.registerRenderer(
  "dropdown",
  "dropdown-lite-question",
  "sv-dropdown-lite-question"
);
